import {
  useEffect,
  useState,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react'
import ReactPlayer from 'react-player'
import {
  StyleActionBg,
  StyleActionWrapper,
  StyleAfterAnswerColumn,
  StyleAfterAnswerWrapper,
  StyleBeforeAnswerColumn,
  StyleBeforeAnswerImageWrapper,
  StyleBeforeAnswerWrapper,
  StyleScreenWrapper,
  StyleLargeScreenWrapper,
  StyleAfterAnswerSenderScreen,
} from './style'
import { StyleColumn } from './style'
import Circle from '@components/circle'
import Svg from '@components/svg'
import Image from '@components/image'
import Typography from '@components/typography'
import usePeerConnection from '@hooks/usePeerConnection'
import { useSocket } from '@providers/SocketProvider'
import { useSelector } from 'react-redux'
import { Helper } from '@utils'
const Screen = () => {
  const peerConnection = usePeerConnection()
  const { data: user } = useSelector((state: any) => state?.user)
  const sender = {
    id: user?.id,
    name: user?.name,
    avatar: user?.avatar,
  }
  const state = Helper.getCallSession()
  const {
    receiver,
    callType,
    type,
    offer,
    conversationId,
    callReceiverId,
    callSenderId,
  } = state
  const { socket }: any = useSocket()
  const shouldEmitCallEndRef = useRef(true)
  const callRef = useRef(null)
  const [localStream, setLocalStream] = useState<any>(null)
  const [remoteStream, setRemoteStream] = useState<any>(null)
  const [candidate, setCandidate] = useState<any>(null)
  const [isAnswer, setIsAnswer] = useState<boolean>(false)
  const [secondsElapsed, setSecondsElapsed] = useState(0)
  const [actionInfos, setActionInfos] = useState({
    local: {
      isVideo: callType === 'video' ? true : false,
      isMicrophone: true,
    },
    remote: {
      isVideo: callType === 'video' ? true : false,
      isMicrophone: true,
    },
  })
  const [outgoinAudio] = useState(
    new Audio(require('@assets/outgoing_ring.mp3')),
  )

  const formatTime = (seconds: any) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secondsLeft = seconds % 60
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0',
    )}:${String(secondsLeft).padStart(2, '0')}`
  }

  const playOutgoingAudio = () => {
    outgoinAudio.loop = true
    outgoinAudio.volume = 0.7
    outgoinAudio.play().catch((err) => {
      console.error('Audio playback failed:', err)
    })
  }

  const stopOutgoinAudio = () => {
    outgoinAudio.pause()
    outgoinAudio.currentTime = 0
  }

  const resetAfterEndCall = async () => {
    await localStream?.getTracks()?.forEach((track: any) => {
      track.stop()
    })
    setIsAnswer(false)
    setLocalStream(null)
    setRemoteStream(null)
    window.close()
  }

  const startLocalStream = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: true,
    })
    const tracks = stream?.getTracks()
    tracks?.forEach((track: any) => {
      peerConnection.addTrack(track, stream)
    })

    setLocalStream(stream)
    return stream
  }

  const handleRemoteStreamListener = (event: any) => {
    if (event.streams && event.streams[0]) {
      setRemoteStream(event.streams[0])
    }
  }

  const handleSendCallRequest = async () => {
    await startLocalStream()
    const sessionDescription = await peerConnection.createOffer({
      mandatory: {
        OfferToReceiveAudio: true,
        OfferToReceiveVideo: true,
        VoiceActivityDetection: true,
      },
    })
    await peerConnection.setLocalDescription(sessionDescription)
    const payload: any = {
      sender: sender,
      receiver: receiver,
      callType: callType,
      offer: sessionDescription,
      conversationId: conversationId,
      callSenderId: callSenderId,
      callReceiverId: callReceiverId,
    }
    playOutgoingAudio()
    socket.emit('sendCallRequest', payload)
  }

  const handleCallAnswer = async () => {
    setIsAnswer(true)
    await startLocalStream()
    await peerConnection.setRemoteDescription(new RTCSessionDescription(offer))
    const sessionDescription = await peerConnection.createAnswer()
    await peerConnection.setLocalDescription(sessionDescription)
    peerConnection.addIceCandidate(candidate)
    socket.emit('sendCallAnswer', {
      receiverId: receiver?.id,
      offer: sessionDescription,
      conversationId: conversationId,
    })
  }

  const handleIceCandidate = async (event) => {
    if (event?.candidate) {
      socket.emit('sendIceCandidate', {
        receiverId: receiver?.id,
        candidate: event.candidate,
      })
    }
  }

  const handleCallEnd = async () => {
    resetAfterEndCall()
  }

  const handleCallAction = async (action: string) => {
    switch (action) {
      case 'end':
        handleCallEnd()
        break
      case 'video':
        const updateVideoActions: any = {
          ...actionInfos,
          local: {
            ...actionInfos?.local,
            isVideo: !actionInfos?.local?.isVideo,
          },
        }
        localStream.getVideoTracks().forEach((track: any) => {
          track.enabled = updateVideoActions?.local.isVideo
        })
        setActionInfos(updateVideoActions)
        socket.emit('sendCallAction', {
          receiverId: receiver?.id,
          callActions: updateVideoActions,
        })
        break
      case 'audio':
        const updateMicrophoneActions = {
          ...actionInfos,
          local: {
            ...actionInfos?.local,
            isMicrophone: !actionInfos?.local?.isMicrophone,
          },
        }
        localStream.getAudioTracks().forEach((track: any) => {
          track.enabled = updateMicrophoneActions?.local?.isMicrophone
        })
        setActionInfos(updateMicrophoneActions)
        socket.emit('sendCallAction', {
          receiverId: receiver?.id,
          callActions: updateMicrophoneActions,
        })
        break
    }
  }

  const onCallEnd = useCallback(() => {
    shouldEmitCallEndRef.current = false
    resetAfterEndCall()
  }, [])

  const onCallAnswered = useCallback(async (data) => {
    setIsAnswer(true)
    await peerConnection.setRemoteDescription(
      new RTCSessionDescription(data.offer),
    )
    stopOutgoinAudio()
  }, [])

  const onCallIceCandidate = useCallback(async (data: any) => {
    if (data?.candidate) {
      if (type == 'outgoing') {
        peerConnection.addIceCandidate(data?.candidate)
      } else {
        setCandidate(data?.candidate)
      }
    }
  }, [])

  const onCallAction = useCallback(async (data: any) => {
    setActionInfos({
      local: data?.remote,
      remote: data?.local,
    })
  }, [])

  const handleBeforeUnload = () => {
    if (shouldEmitCallEndRef.current) {
      socket.emit('sendCallEnd', {
        callSenderId: callSenderId,
        callReceiverId: callReceiverId,
        receiverId: receiver?.id,
        duration: {
          startTime: callRef.current.startTime,
          endTime: new Date(),
        },
        call: callType,
        conversationId: conversationId,
        status: !!callRef.current?.isAnswer ? 'Answered' : 'No Response',
      })
    }
    peerConnection.close()
  }

  useEffect(() => {
    peerConnection.addEventListener('track', handleRemoteStreamListener)
    peerConnection.addEventListener('icecandidate', handleIceCandidate)

    return () => {
      peerConnection.removeEventListener('track', handleRemoteStreamListener)
      peerConnection.removeEventListener('icecandidate', handleIceCandidate)
      stopOutgoinAudio()
      peerConnection.close()
    }
  }, [])

  useLayoutEffect(() => {
    socket.on('onCallEnd', onCallEnd)
    socket.on('onCallAnswered', onCallAnswered)
    socket.on('onIceCandidate', onCallIceCandidate)
    socket.on('onCallAction', onCallAction)
    return () => {
      socket.off('onCallEnd', onCallEnd)
      socket.off('onCallAnswered', onCallAnswered)
      socket.off('onIceCandidate', onCallIceCandidate)
      socket.off('onCallAction', onCallAction)
    }
  }, [socket])

  useEffect(() => {
    if (!!isAnswer) {
      let timer: any
      callRef.current = {
        startTime: new Date(),
        isAnswer: true,
      }
      setInterval(() => {
        setSecondsElapsed((prevSeconds) => prevSeconds + 1)
      }, 1000)
      return () => {
        clearInterval(timer)
      }
    }
  }, [isAnswer])

  useEffect(() => {
    if (type == 'outgoing') {
      handleSendCallRequest()
    }
    if (type == 'incoming') {
      handleCallAnswer()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return (
    <StyleColumn>
      <StyleScreenWrapper>
        <StyleLargeScreenWrapper>
          {!isAnswer && (
            <Image
              src={`${process.env.BUCKET_URL}/${sender?.avatar}`}
              styleImage={`filter:blur(${!isAnswer ? '4px' : '0px'})`}
              fallbackType="userBanner"
            />
          )}
          {!!isAnswer && (
            <>
              {!!remoteStream && (
                <>
                  <ReactPlayer
                    playing
                    muted={false}
                    height="100%"
                    width="100%"
                    url={remoteStream}
                    style={{
                      display: !!actionInfos?.remote?.isVideo
                        ? 'block'
                        : 'none',
                    }}
                  />
                  <Image
                    src={`${process.env.BUCKET_URL}/${receiver?.avatar}`}
                    styleImage={`filter:blur(${!isAnswer ? '4px' : '0px'});display:${!actionInfos?.remote?.isVideo ? 'block' : 'none'}`}
                    fallbackType="userBanner"
                  />
                </>
              )}
            </>
          )}
        </StyleLargeScreenWrapper>
        {!isAnswer && (
          <StyleBeforeAnswerWrapper>
            <StyleBeforeAnswerColumn>
              <StyleBeforeAnswerImageWrapper>
                <Image
                  fallbackType="user"
                  src={`${process.env.BUCKET_URL}/${receiver?.avatar}`}
                />
              </StyleBeforeAnswerImageWrapper>
              <Typography
                content={receiver?.name}
                styleText="text-align:center;"
                size={16}
                tc="white"
                weight={400}
              />
              <Typography
                content={'Waiting for Patient to Join the Call...'}
                styleText="text-align:center;"
                size={16}
                tc="white"
              />
            </StyleBeforeAnswerColumn>
          </StyleBeforeAnswerWrapper>
        )}
        {!!isAnswer && (
          <StyleAfterAnswerWrapper>
            <StyleAfterAnswerColumn>
              <Typography
                content={receiver?.name}
                tc="white"
                size={16}
                styleText="text-align:center;"
              />
              <Typography
                content={formatTime(secondsElapsed)}
                tc="white"
                size={16}
                styleText="text-align:center;"
              />
            </StyleAfterAnswerColumn>
            <StyleAfterAnswerSenderScreen>
              {!!localStream && (
                <>
                  <ReactPlayer
                    playing
                    muted={true}
                    height="100%"
                    width="100%"
                    url={localStream}
                    style={{
                      display: !!actionInfos?.local?.isVideo ? 'block' : 'none',
                    }}
                  />
                  <Image
                    src={`${process.env.BUCKET_URL}/${sender?.avatar}`}
                    styleImage={`display:${!actionInfos?.local?.isVideo ? 'block' : 'none'}`}
                    fallbackType="userBanner"
                  />
                </>
              )}
            </StyleAfterAnswerSenderScreen>
          </StyleAfterAnswerWrapper>
        )}
      </StyleScreenWrapper>
      <StyleActionWrapper>
        <StyleActionBg>
          <Circle
            bg={!!actionInfos?.local?.isVideo ? 'secondary' : 'white'}
            size={40}
            onClick={() => {
              handleCallAction('video')
            }}
          >
            <Svg
              svg={'video'}
              color={!!actionInfos?.local?.isVideo ? 'white' : 'text'}
            />
          </Circle>
          <Circle
            bg={!!actionInfos?.local?.isMicrophone ? 'secondary' : 'white'}
            size={40}
            onClick={() => {
              handleCallAction('audio')
            }}
          >
            <Svg
              svg={'mike'}
              color={!!actionInfos?.local?.isMicrophone ? 'white' : 'text'}
            />
          </Circle>
          {user?.role == 'doctor' && (
            <Circle bg={'white'} size={40}>
              <Svg svg={'consult'} />
            </Circle>
          )}
          <Circle bg={'white'} size={40}>
            <Svg svg={'message'} />
          </Circle>
          <Circle
            bg={'danger'}
            size={40}
            onClick={() => {
              handleCallAction('end')
            }}
          >
            <Svg svg={'call'} color={'white'} />
          </Circle>
        </StyleActionBg>
      </StyleActionWrapper>
    </StyleColumn>
  )
}

export default Screen
